import { z } from 'zod';
import type {
  CoverShipmentDepartedEventPayload,
  CoverShipmentEvidenceUpdatedEventPayload,
  CoverShipmentSavedEventPayload,
  CoverShipmentVoidedEventPayload,
} from './cover-shipments-sync.dto.js';
import { CoverShipmentAttachmentItemSchema } from '../../cover-shipments/cover-shipments-shared.schema.js';
import { CounterpartySnapshotSchema } from '../../counterparties/counterparties.schema.js';
import {
  ShipmentBuilderInputSchema,
  ShipmentSourceEvidenceSchema,
  ShipmentTotalsSummarySchema,
  validateShipmentSourceEvidencePair,
} from '../../shipments/shipments-shared.schema.js';
import { IsoDateTimeSchema } from '../../shipments/shipments-types.js';
import { CoverSourcePreparationRequestDetailSchema } from '../../source-preparation/source-preparation.schema.js';

const CoverShipmentSyncPayloadBaseSchema = z
  .object({
    schemaVersion: z.literal(1),
    eventId: z.uuid(),
    occurredAt: IsoDateTimeSchema,
    companyId: z.uuid(),
    stationId: z.uuid(),
    coverShipmentId: z.uuid(),
  })
  .strict();

export const CoverShipmentSavedEventPayloadSchema: z.ZodType<CoverShipmentSavedEventPayload> =
  CoverShipmentSyncPayloadBaseSchema.extend({
    actorUserId: z.uuid().nullable(),
    materialId: z.uuid(),
    shipper: CounterpartySnapshotSchema,
    receiver: CounterpartySnapshotSchema,
    builderInput: ShipmentBuilderInputSchema,
    sourcePreparationRequestId: z.uuid().nullable().optional(),
    sourcePreparationSnapshotJson: CoverSourcePreparationRequestDetailSchema.nullable().optional(),
    attachments: z.array(CoverShipmentAttachmentItemSchema).nonempty(),
    ownPurchaseLineItemIds: z.array(z.uuid()),
    purchaseCommittedIds: z.array(z.uuid()),
    sourceLoadSessionId: z.uuid().nullable().optional(),
    sourceLoadSessionGroupId: z.uuid().nullable().optional(),
    departedAt: IsoDateTimeSchema.nullable().optional(),
    totals: ShipmentTotalsSummarySchema,
  })
    .strict()
    .superRefine(validateShipmentSourceEvidencePair);

export const CoverShipmentVoidedEventPayloadSchema: z.ZodType<CoverShipmentVoidedEventPayload> =
  CoverShipmentSyncPayloadBaseSchema.extend({
    actorUserId: z.uuid().nullable(),
    reason: z.string().trim().min(1),
  }).strict();

export const CoverShipmentEvidenceUpdatedEventPayloadSchema: z.ZodType<CoverShipmentEvidenceUpdatedEventPayload> =
  CoverShipmentSyncPayloadBaseSchema.extend({
    actorUserId: z.uuid().nullable(),
    ...ShipmentSourceEvidenceSchema.shape,
  })
    .strict()
    .superRefine(validateShipmentSourceEvidencePair);

export const CoverShipmentDepartedEventPayloadSchema: z.ZodType<CoverShipmentDepartedEventPayload> =
  CoverShipmentSyncPayloadBaseSchema.extend({
    actorUserId: z.uuid().nullable(),
    departedAt: IsoDateTimeSchema,
  }).strict();
